import { leagueFetch } from '@leagueplatform/league-fetch';
import { SocketAsFetch } from '@leagueplatform/socket-as-fetch';
import { isLoginRequiredError } from '@leagueplatform/auth';
import { captureError } from '@leagueplatform/observability';
import {
  AnalyticsAuthAbortError,
  type AnalyticsSdkEvent,
  type Transport,
} from './analytics-sdk.types';

type RestTransportOptions = { tenantId?: string };

const createWsTransport = (): Transport => ({
  send: async (batch: AnalyticsSdkEvent[]): Promise<AnalyticsSdkEvent[]> => {
    for (let i = 0; i < batch.length; i += 1) {
      const event = batch[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await SocketAsFetch.fetch(
          {
            message_type: 'set_analytics_event',
            info: {
              name: event.eventName,
              params: event.props,
              echo: true,
            },
          },
          {
            onInternalError: (error) => {
              throw error;
            },
          },
        );
      } catch (error) {
        if (isLoginRequiredError(error))
          throw new AnalyticsAuthAbortError(batch.slice(i));
        return batch.slice(i);
      }
    }

    return [];
  },
});

const createRestTransport = (options?: RestTransportOptions): Transport => ({
  send: async (batch: AnalyticsSdkEvent[]): Promise<AnalyticsSdkEvent[]> => {
    if (batch.length === 0) return [];

    const { tenantId } = options ?? {};

    let body: string;
    try {
      body = JSON.stringify({
        data: {
          type: 'analytics-event-batch',
          attributes: {
            events: batch.map((event) => ({
              name: event.eventName,
              params: event.props,
            })),
          },
        },
      });
    } catch (err) {
      captureError(
        err instanceof Error
          ? err
          : new Error('analytics batch serialization failed'),
      );
      return [];
    }

    let response: Response;
    try {
      response = await leagueFetch('/v1/analytics-events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          ...(tenantId && { 'X-League-Tenant-Id': tenantId }),
        },
        body,
        skipErrorCapture: true,
      });
    } catch (error) {
      if (isLoginRequiredError(error)) throw new AnalyticsAuthAbortError(batch);
      return batch;
    }

    if (response.status === 401) throw new AnalyticsAuthAbortError(batch);
    if (!response.ok) {
      if (response.status === 429 || response.status >= 500) return batch;
      if (response.status >= 400) {
        captureError(
          new Error(
            `Analytics REST transport: dropped batch on ${response.status}`,
          ),
        );
      }
      return [];
    }

    return [];
  },
});

export const createTransport = (
  flag?: 'websocket' | 'rest',
  options?: RestTransportOptions,
): Transport =>
  flag === 'rest' ? createRestTransport(options) : createWsTransport();
